<template>
  <div v-if="showTeamMenu">
    <v-app-bar height="76" flat color="black" class="team-bar" :style="{ '--blob-color': teamHex }">
      <v-container max-width="1300">
        <div class="title-section">
          <div class="ios-row-icon team-banner-icon">
            <img :src="getTeamImage(teamData?.imageUrl)" alt="Team logo" />
          </div>
          <div class="title-wrap">
            <span class="team-name-bar">{{ teamData?.name }}</span>
          </div>

          <v-tabs v-model="activeSection" class="px-0 d-none d-sm-flex" :slider-color="teamHex">
            <v-tab :to="`/teams/${teamData?._id}/schedule`" key="schedule" class="my-tab"
              :text="$t('schedule')" />
            <v-tab :to="`/teams/${teamData?._id}/members`" key="members" class="my-tab"
              :text="$t('nav.members')" />
            <v-tab :to="`/teams/${teamData?._id}/treasury`" key="treasury" class="my-tab"
              :text="$t('treasury')" />
          </v-tabs>
        </div>
      </v-container>
    </v-app-bar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { PropType } from 'vue';
import { useRoute } from 'vue-router';
import { getTeamImage } from '@/utils/imageUtils';

const route = useRoute();

const props = defineProps({
  teamData: {
    type: Object as PropType<{
      _id: string;
      name: string;
      imageUrl?: string;
      primaryTeamColor?: string;
      secondaryTeamColor?: string;
    } | null>,
    default: null,
  },
});

const ICE = '#00D4FF';

function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function contrastOnBlack(hex: string): number {
  return (luminance(hex) + 0.05) / 0.05;
}

const teamHex = computed(() => {
  const primary = props.teamData?.primaryTeamColor || '#dc2626';
  if (contrastOnBlack(primary) >= 2) return primary;
  const secondary = props.teamData?.secondaryTeamColor;
  if (secondary && contrastOnBlack(secondary) >= 2) return secondary;
  return ICE;
});

const showTeamMenu = computed(() => route.path.match(/^\/teams\/.+/));

const activeSection = ref(0);
</script>

<style scoped>
.team-bar {
  overflow: hidden;
  border-bottom: none !important;
}

.team-bar::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, var(--blob-color), transparent);
  pointer-events: none;
  z-index: 1;
}

.v-tab.my-tab {
  height: 30px !important;
}

.title-section {
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 10px;
  text-align: center;
  padding: 3px 0;
  font-size: 0.9rem;
}

.title-wrap {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  margin-right: 8px;
}

.team-name-bar {
  font-family: 'Barlow Condensed', 'Roboto Condensed', sans-serif;
  font-weight: 900;
  font-size: 22px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #ffffff;
}

.team-banner-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  margin-right: 0;
}
</style>
